import React from 'react';
import {useLocation, useParams} from 'react-router-dom';
import getStoreRecordsByMask from '../../utilities/get-store-records-by-mask';
import {graphql, useMutation} from 'react-relay/hooks';
import Button, {LinkButton, RouterLinkButton} from '../../components/button/button';
import searchFilter from '../../searchFilter';
import LogoOttryBusinessDarkModeFalse from '../../assets/logo-ottry-business-dark-mode-false.svg';
import {FormattedMessage} from 'react-intl';
import AlertTriangleIcon from '../../assets/icons0/alert-triangle.svg';
import AlertCircleIcon from '../../assets/icons0/alert-circle.svg';
import CheckCircleIcon from '../../assets/icons0/check-circle.svg';
import LogoLoading from '../../assets/logo-loading.svg';
import './share-access.scss';
import IconFeature from '../../components/icon-feature/icon-feature';
import {TELEGRAM_SUPPORT} from '../../constants';
import {queryParameters} from '../../qs';
import searchUnFilter from '../../searchUnFilter';

const RAZZLE_APP_ACCOUNTS = process.env.RAZZLE_APP_ACCOUNTS;

const STATUS_DESCRIPTION = {
    'error-user': {
        icon: <AlertTriangleIcon/>,
        iconColor: 'warning',
        title: <FormattedMessage defaultMessage='Incorrect account'/>,
        description: <FormattedMessage defaultMessage='The account that was granted access does not match the current one. If you were not granted access, click cancel'/>,
        actions: {
            showSupport: false,
            showBusinessCabinet: false,
            showCancel: true,
            showChangeAccount: true
        }
    },
    'error-contract': {
        icon: <AlertCircleIcon/>,
        iconColor: 'error',
        title: <FormattedMessage defaultMessage='Access already granted'/>,
        description: <FormattedMessage defaultMessage='The access link has already been used. Go to your business account or contact support.'/>,
        actions: {
            showSupport: true,
            showBusinessCabinet: true,
            showCancel: false,
            showChangeAccount: false
        }
    },
    'success-': {
        icon: <CheckCircleIcon/>,
        iconColor: 'success',
        title: <FormattedMessage defaultMessage='Successfull'/>,
        description: <FormattedMessage defaultMessage='You have gained access to the business cabinet'/>,
        actions: {
            showSupport: false,
            showBusinessCabinet: true,
            showCancel: false,
            showChangeAccount: false
        }
    }
}

const ShareAccess = () => {
    const {contractId} = useParams();
    const location = useLocation();
    const [status, setStatus] =  React.useState('pending');
    const [errorType, setErrorType] =  React.useState('');
    const [acceptContract, acceptingContract] = useMutation(
        graphql`
            mutation shareAccessPagesAcceptWithMyselfMutation($request: AcceptWithMyselfRequestInput) {
                acceptWithMyself(request: $request) {
                    client {
                        id
                    }
                    clientEmail
                    id
                    merchant {
                        id
                    }
                    note
                    role
                    status
                    version
                }
            }
    `);
    const accept = () => {
        acceptContract({
            variables: {request: {
                id: contractId,
            }},
            onCompleted: (data) => {
                setStatus('success');
                setErrorType('');
            },
            onError: (err) => {
                setStatus('error');
                if (err.toString().includes('Contract invalid')) {
                    setErrorType('contract');
                }
                if (err.toString().includes('User invalid')) {
                    setErrorType('user');
                }
            },
            updater: store => {
                const recordIds = getStoreRecordsByMask(store, 'contractsByClient');
                if (recordIds.length) {
                    recordIds.forEach(id => {
                        store.get(id).invalidateRecord();
                    })
                }
            }
        });
    };
    React.useEffect(() => {
        accept();
    }, []);
    const changeAccount = () => {
        const qps = queryParameters(location);
        setTimeout(() => window.location.href = RAZZLE_APP_ACCOUNTS 
            + '/endpoint?cache=0&new=true&continue='
            + encodeURIComponent(location.pathname + searchUnFilter(document.location.search))
            + (qps.length ? '&' + qps.join('&') : ''),
            500);
    };
    return (
        <div className='pages'>
            <div className='share-access'>
                <div className='display-flex align-items-flex-end position-relative width-100percent min-height-100dvh'>
                    <div className='position-absolute width-100percent padding-left-1rem padding-right-1rem top-50percent left-50percent mw768-max-width-37rem transform-translate-center display-flex flex-direction-column align-items-center text-align-center'>
                        {status === 'pending' &&
                            <div className='ogle-logo-loading'>
                                <LogoLoading className='display-block width-3rem height-3rem'/>
                            </div>
                        }
                        {status !== 'pending' && 
                            <>
                                <IconFeature {...{size: 'lg', mw768Size: 'xl', color: STATUS_DESCRIPTION[status + '-' + errorType].iconColor, feature: 'outline'}}>
                                    {STATUS_DESCRIPTION[status + '-' + errorType].icon}
                                </IconFeature>
                                <p className='color-gray-900 padding-top-1rem mw768-padding-top-0dot75rem mw768-padding-top-1dot5rem text-lg medium mw768-display-xs mw768-semibold'>{STATUS_DESCRIPTION[status + '-' + errorType].title}</p>
                                <p className='padding-top-0dot5rem text-sm color-gray-500'>{STATUS_DESCRIPTION[status + '-' + errorType].description}</p>
                                <div className='mw768-max-width-25rem width-100percent padding-top-1dot5rem mw768-padding-top-2rem display-flex flex-direction-column-row column-gap-0dot75rem row-gap-0dot75rem'>
                                    {STATUS_DESCRIPTION[status + '-' + errorType].actions.showChangeAccount &&
                                        <div className='flex-1 mw768-order-2'>
                                            <Button {...{size: 'lg', color: 'primary-gradient', clickHandler: changeAccount, fluid: 'always'}}>
                                                <FormattedMessage defaultMessage='Change account'/>
                                            </Button>
                                        </div>
                                    }
                                    {STATUS_DESCRIPTION[status + '-' + errorType].actions.showCancel &&
                                        <div className='flex-1 mw768-order-1'>
                                            <RouterLinkButton {...{size: 'lg', color: 'secondary-gray', to: `/business${searchFilter(location.search)}`, fluid: 'always'}}>
                                                <FormattedMessage defaultMessage='Cancel'/>
                                            </RouterLinkButton>
                                        </div>
                                    }
                                    {STATUS_DESCRIPTION[status + '-' + errorType].actions.showBusinessCabinet &&
                                        <div className='flex-1 mw768-order-2'>
                                            <RouterLinkButton {...{size: 'lg', color: 'primary-gradient', to: `/business${searchFilter(location.search)}`, fluid: 'always'}}>
                                                <FormattedMessage defaultMessage='To business cabinet'/>
                                            </RouterLinkButton>
                                        </div>
                                    }
                                    {STATUS_DESCRIPTION[status + '-' + errorType].actions.showSupport &&
                                        <div className='flex-1 mw768-order-1'>
                                            <LinkButton {...{link: {target: '_blank', rel: 'noopener noreferrer', href: TELEGRAM_SUPPORT}, color: 'secondary-gray', size:'lg', fluid: 'always'}} >
                                                <FormattedMessage defaultMessage='Support'/>
                                            </LinkButton>
                                        </div>
                                    }
                                </div>
                            </>
                        }
                    </div>
                    <div className='padding-bottom-1dot25rem mw768-padding-bottom-2dot5rem display-flex justify-content-center width-100percent'>
                        <LogoOttryBusinessDarkModeFalse className='display-block height-2rem'/>
                    </div>
                </div>
            </div>
        </div>
    )
};

export default React.memo(ShareAccess);